import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Phone, Mail, MapPin, ShieldCheck, Clock, HeartHandshake,
  FileCheck2, Headset, Globe2, ArrowRight, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { QuoteWizard } from "@/components/QuoteWizard";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Website() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [legal, setLegal] = useState(null);
  const [paidTracking, setPaidTracking] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const sessionId = params.get("session_id");
    if (paid || sessionId) {
      window.history.replaceState({}, "", window.location.pathname);
      if (sessionId) {
        api.get(`/public/confirm-payment/${sessionId}`)
          .then(({ data }) => setPaidTracking(data.tracking_code || paid))
          .catch(() => setPaidTracking(paid));
      } else {
        setPaidTracking(paid);
      }
    }
  }, []);

  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const seo = settings?.seo;
    if (!seo) return;
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    if (seo.title) document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("name", "keywords", seo.keywords);
    setMeta("property", "og:title", seo.og_title || seo.title);
    setMeta("property", "og:description", seo.og_description || seo.description);
    if (seo.og_image) setMeta("property", "og:image", seo.og_image);
    setMeta("property", "og:type", "website");
  }, [settings]);

  const track = async (e) => {
    e.preventDefault(); setError(""); setResult(null);
    if (!code.trim()) return;
    try { const { data } = await api.get(`/public/track/${code.trim().toUpperCase()}`); setResult(data); }
    catch { setError(t("track.notfound")); }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      {settings?.maintenance_mode && (
        <div className="fixed inset-0 z-[2000] bg-stone-900 text-stone-100 flex flex-col items-center justify-center px-6 text-center" data-testid="maintenance-screen">
          <div className="font-display text-3xl font-bold mb-3">Rimpatrio<span className="text-amber-400">24</span></div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mb-3">Sito in manutenzione</h1>
          <p className="text-stone-300 max-w-md text-sm">Stiamo effettuando alcuni interventi tecnici. Torneremo online a breve. Per urgenze contattateci telefonicamente.</p>
          <a href="/login" className="mt-8 text-xs text-stone-400 hover:text-stone-200 underline" data-testid="maintenance-login-link">Accesso staff</a>
        </div>
      )}
      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-[1100] transition-colors duration-300 ${scrolled ? "bg-stone-50/90 backdrop-blur-md border-b border-stone-200" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
            {settings?.branding?.compact_logo || settings?.branding?.website_logo ? (
              <span className={`flex items-center rounded-lg transition-colors ${scrolled ? "" : "bg-white/95 px-2.5 py-1.5 shadow-sm"}`}>
                <img src={settings.branding.compact_logo || settings.branding.website_logo} alt="Rimpatrio24" className="h-9 w-auto object-contain" />
              </span>
            ) : (
              <span className={`font-serif-display text-2xl font-semibold tracking-tight ${scrolled ? "text-stone-900" : "text-white"}`}>
                Rimpatrio<span className="text-[#b08d4f]">24</span>
              </span>
            )}
          </Link>
          <nav className={`hidden md:flex items-center gap-8 text-sm font-medium ${scrolled ? "text-stone-600" : "text-white/80"}`}>
            <a href="#how" className="hover:opacity-70">{t("home.howTitle")}</a>
            <a href="#services" className="hover:opacity-70">{t("nav.services")}</a>
            <a href="#tracking" className="hover:opacity-70">{t("nav.tracking")}</a>
            <a href="#contact" className="hover:opacity-70">{t("nav.contact")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <div className={scrolled ? "" : "[&_button]:text-white [&_button:hover]:bg-white/10"}><LanguageSwitcher /></div>
            {user ? (
              <Link to="/dashboard"><Button size="sm" className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="nav-dashboard">{t("nav.dashboard")}</Button></Link>
            ) : (
              <Link to="/login"><Button size="sm" variant={scrolled ? "outline" : "ghost"} className={scrolled ? "" : "text-white hover:bg-white/10"} data-testid="nav-login">{t("nav.login")}</Button></Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero with wizard */}
      <section className="relative pt-28 pb-20 lg:pt-32 lg:pb-28">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/32846096/pexels-photo-32846096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/95 via-stone-950/92 to-stone-950/88" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:pt-10 text-white">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#d8c28a]">
              <Clock size={14} /> {t("home.badge")}
            </span>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] tracking-tight mt-5 text-white">
              {t("hero.title")}
            </h1>
            <p className="text-lg text-stone-100 mt-6 leading-relaxed max-w-xl">{t("home.subhero")}</p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-stone-100">
              <span className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#d8c28a]" /> {t("home.b.op247")}</span>
              <span className="flex items-center gap-2"><Globe2 size={18} className="text-[#d8c28a]" /> {t("home.b.europe")}</span>
              <span className="flex items-center gap-2"><HeartHandshake size={18} className="text-[#d8c28a]" /> {t("home.b.dignity")}</span>
            </div>
            <a href={`tel:${(settings?.company?.emergency_phone || settings?.company?.phone || "").replace(/\s/g, "")}`}
               className="mt-9 inline-flex items-center gap-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur px-5 py-3.5 hover:bg-white/15 transition-colors"
               data-testid="hero-phone-block">
              <span className="h-10 w-10 rounded-full bg-[#b08d4f] flex items-center justify-center shrink-0"><Phone size={18} className="text-stone-950" /></span>
              <span className="text-left">
                <span className="block text-[11px] uppercase tracking-wider text-[#d8c28a]">Assistenza Emergenze 24/7</span>
                <span className="block text-lg font-semibold text-white leading-tight" data-testid="hero-phone-number">{settings?.company?.emergency_phone || settings?.company?.phone || "+39 320 410 9592"}</span>
              </span>
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} id="quote">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3.5 text-[13px] font-medium text-white/90" data-testid="hero-trust-bar">
              {[t("home.tb.quote2min"), t("home.tb.avail247"), t("home.tb.docsIncl"), t("home.tb.securePay")].map((b) => (
                <span key={b} className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> {b}</span>
              ))}
            </div>
            {settings ? <QuoteWizard settings={settings} /> : <div className="bg-white rounded-2xl h-[520px] animate-pulse" />}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a6d2f]">{t("home.howTitle")}</span>
            <h2 className="font-serif-display text-3xl lg:text-4xl font-semibold tracking-tight mt-3">{t("home.reassureTitle")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: FileCheck2, t: t("home.how1t"), d: t("home.how1d") },
              { icon: Headset, t: t("home.how2t"), d: t("home.how2d") },
              { icon: HeartHandshake, t: t("home.how3t"), d: t("home.how3d") },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-stone-200 p-7 text-center">
                <span className="h-14 w-14 rounded-full bg-[#f5efe2] text-[#8a6d2f] flex items-center justify-center mx-auto"><s.icon size={24} strokeWidth={1.5} /></span>
                <div className="text-xs text-stone-400 mt-4">0{i + 1}</div>
                <h3 className="font-serif-display text-xl mt-1">{s.t}</h3>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet banner — real partner vehicles */}
      <section id="fleet" className="relative">
        <div className="relative h-[420px] md:h-[560px] w-full overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_repatriation-hub/artifacts/4e15fole_989pgdq9_2.jpg"
            alt={t("home.fleetTitle")}
            className="absolute inset-0 w-full h-full object-cover object-center"
            data-testid="fleet-banner-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent" />
          <div className="relative h-full max-w-6xl mx-auto px-6 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="max-w-xl" data-testid="fleet-banner-content">
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#d4af6a]">{t("home.fleetKicker")}</span>
              <h2 className="font-serif-display text-3xl lg:text-5xl font-semibold tracking-tight text-white mt-3 leading-tight">{t("home.fleetTitle")}</h2>
              <p className="text-stone-200 mt-5 leading-relaxed text-base lg:text-lg">{t("home.fleetBody")}</p>
              <a href="#quote"><Button className="mt-8 bg-blue-900 hover:bg-blue-800 h-12 px-7 text-base" data-testid="fleet-banner-cta">{t("hero.cta")} <ArrowRight size={18} className="ml-2" /></Button></a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reassurance band */}
      <section className="py-20 px-6 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif-display text-3xl lg:text-4xl font-semibold tracking-tight">{t("home.reassureTitle")}</h2>
            <p className="text-stone-300 mt-5 leading-relaxed text-lg">{t("home.reassureBody")}</p>
            <a href="#quote"><Button className="mt-8 bg-blue-900 hover:bg-blue-800 h-12 px-7 text-base">{t("hero.cta")} <ArrowRight size={18} className="ml-2" /></Button></a>
          </div>
          <img src="https://images.pexels.com/photos/6918507/pexels-photo-6918507.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="rounded-2xl shadow-2xl w-full h-80 object-cover" />
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8a6d2f]">{t("nav.services")}</span>
            <h2 className="font-serif-display text-3xl lg:text-4xl font-semibold tracking-tight mt-3">{t("services.title")}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {settings?.services?.map((sv, i) => (
              <motion.div key={sv.code} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="bg-white border border-stone-200 rounded-2xl p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <h3 className="font-serif-display text-xl">{sv.name?.[locale] || sv.name?.en}</h3>
                <p className="text-sm text-stone-400 mt-2">{t("wiz.from")} {new Intl.NumberFormat("it-IT", { style: "currency", currency: settings?.currency || "EUR", maximumFractionDigits: 0 }).format(sv.base_price)}*</p>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-stone-400 text-center mt-8 max-w-2xl mx-auto" data-testid="services-price-note">{t("services.priceNote")}</p>
        </div>
      </section>

      {/* Tracking */}
      <section id="tracking" className="py-20 px-6 bg-stone-100 border-y border-stone-200">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif-display text-3xl lg:text-4xl font-semibold tracking-tight">{t("track.title")}</h2>
          <form onSubmit={track} className="mt-8 flex gap-2 max-w-xl mx-auto">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("track.placeholder")} className="h-12 bg-white" data-testid="track-input" />
            <Button type="submit" className="h-12 px-6 bg-stone-900 hover:bg-stone-800" data-testid="track-submit"><Search size={18} className="mr-1.5" /> {t("track.button")}</Button>
          </form>
          {error && <p className="text-red-600 text-sm mt-4" data-testid="track-error">{error}</p>}
          {result && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white rounded-2xl border border-stone-200 shadow-sm p-6 text-left" data-testid="track-result">
              <div className="flex items-center justify-between"><div className="font-serif-display text-xl">{result.number}</div><StatusBadge status={result.status} /></div>
              <p className="text-sm text-stone-500 mt-1">{result.origin} → {result.destination}</p>
              <div className="mt-6 space-y-4">
                {result.timeline?.slice().reverse().map((ev, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="h-2.5 w-2.5 rounded-full bg-[#b08d4f] mt-1.5" />
                    <div><div className="flex items-center gap-2"><StatusBadge status={ev.status} /><span className="text-xs text-stone-400">{formatDateTime(ev.timestamp)}</span></div>{ev.note && <p className="text-sm text-stone-600 mt-1">{ev.note}</p>}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-stone-950 text-stone-300 py-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
          <div>
            {settings?.branding?.website_logo || settings?.branding?.compact_logo ? (
              <span className="inline-flex bg-white/95 rounded-lg px-3 py-2"><img src={settings.branding.website_logo || settings.branding.compact_logo} alt="Rimpatrio24" className="h-12 w-auto object-contain" /></span>
            ) : (
              <div className="font-serif-display text-2xl text-white">Rimpatrio<span className="text-[#caa86a]">24</span></div>
            )}
            <p className="text-sm mt-3 text-stone-400">{settings?.company?.legal_name}</p>
            <p className="text-xs mt-1 text-stone-500">P.IVA {settings?.company?.vat_number}{settings?.company?.tax_code ? ` · C.F. ${settings.company.tax_code}` : ""}</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><Phone size={15} /> {settings?.company?.phone}</div>
            {settings?.company?.emergency_phone && <div className="flex items-center gap-2 text-[#caa86a]"><Phone size={15} /> {settings.company.emergency_phone} (Emergenze 24/7)</div>}
            <div className="flex items-center gap-2"><Mail size={15} /> {settings?.company?.email}</div>
            <div className="flex items-center gap-2"><MapPin size={15} /> {settings?.company?.address}</div>
          </div>
          <div className="text-sm text-stone-400">
            <p className="text-white font-medium mb-2">{t("nav.login")}</p>
            <Link to="/login" className="block hover:text-white">{t("auth.login")}</Link>
            <Link to="/register" className="block hover:text-white mt-1">{t("auth.register")}</Link>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
              {[["privacy", "Privacy"], ["terms", "Termini"], ["cookies", "Cookie"], ["gdpr", "GDPR"]].map(([k, lbl]) => (
                (settings?.legal_texts?.[k]?.[locale] || settings?.legal_texts?.[k]?.it) ? (
                  <button key={k} onClick={() => setLegal(k)} className="text-xs text-stone-400 hover:text-white underline-offset-2 hover:underline" data-testid={`footer-legal-${k}`}>{lbl}</button>
                ) : null
              ))}
            </div>
            <p className="mt-6 text-xs text-stone-600">{settings?.company?.copyright_text || `© ${new Date().getFullYear()} Rimpatrio24 — rimpatrio24.it`}</p>
          </div>
        </div>
      </footer>

      {paidTracking && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60" data-testid="paid-confirmation">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg className="h-9 w-9 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="font-serif-display text-2xl text-stone-900 mt-4">{t("paid.title")}</h2>
            <p className="text-sm text-stone-500 mt-2">{t("paid.body")}</p>
            <div className="mt-4 bg-stone-50 rounded-lg px-4 py-3 border border-stone-200">
              <div className="text-xs uppercase tracking-wider text-stone-400">{t("paid.tracking")}</div>
              <div className="font-mono font-semibold text-stone-800">{paidTracking}</div>
            </div>
            <div className="mt-6 flex gap-3 justify-center">
              <a href={`/track/${paidTracking}`}><button className="bg-blue-900 text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-blue-800" data-testid="paid-track-btn">{t("paid.track")}</button></a>
              <button onClick={() => setPaidTracking(null)} className="text-stone-500 hover:text-stone-800 px-4 text-sm" data-testid="paid-close-btn">✕</button>
            </div>
          </div>
        </div>
      )}

      {legal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setLegal(null)} data-testid="legal-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-display text-2xl text-stone-900 capitalize">{legal}</h2>
              <button onClick={() => setLegal(null)} className="text-stone-400 hover:text-stone-700 text-2xl leading-none" data-testid="legal-modal-close">×</button>
            </div>
            <div className="text-sm text-stone-600 whitespace-pre-wrap leading-relaxed">
              {settings?.legal_texts?.[legal]?.[locale] || settings?.legal_texts?.[legal]?.it || "—"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
